import React, { useState, lazy } from 'react';
import { CheckCircle2Icon, Clock3Icon, LinkedinIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { Language } from '../App';
import { SectionHeading } from '../components/SectionHeading';
const WEB3FORMS_ACCESS_KEY = '9f6d2e40-eb5c-4438-9a46-6e175eae72f4';
type Web3FormsResponse = {
  success?: boolean;
  message?: string;
};
export function ContactPage({
  language
}: {
  language: Language;
}) {
  const ar = language === 'ar';
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorType, setErrorType] = useState<'validation' | 'submission' | null>(null);
  const [submissionError, setSubmissionError] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    if (!form.get('name') || !form.get('email') || !form.get('message')) {
      setErrorType('validation');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setErrorType(null);
    setSubmissionError('');
    form.append('access_key', WEB3FORMS_ACCESS_KEY);
    form.append('subject', 'New website enquiry — United Third Financial Company');
    form.append('from_name', 'United Third Financial Company Website');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: form
      });
      const result: Web3FormsResponse = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Web3Forms submission failed');
      }
      formElement.reset();
      setStatus('success');
    } catch (error) {
      setSubmissionError(error instanceof Error ? error.message : 'Web3Forms submission failed');
      setErrorType('submission');
      setStatus('error');
    }
  }
  const label = (en: string, arabic: string) => ar ? arabic : en;
  return <>
      <section className="bg-[#08233f] py-20" data-id="element-74">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-75">
          <SectionHeading language={language} light eyebrow={ar ? 'تواصل معنا' : 'Contact us'} title={ar ? 'لنصنع الفرصة التالية معاً.' : 'Let’s shape the next opportunity together.'} text={ar ? 'تواصل مع فريقنا لمناقشة القروض التجارية واحتياجات التمويل أو الاستثمار الخاصة بك بسرية تامة.' : 'Speak with our team about commercial loans, financing, or investment needs in complete confidence.'} data-id="element-76" />
        </div>
      </section>
      <section className="bg-white py-20" data-id="element-77">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[.85fr_1.15fr] lg:px-8" data-id="element-78">
          <div data-id="element-79">
            <SectionHeading language={language} eyebrow={ar ? 'مكتب جدة' : 'Jeddah office'} title={ar ? 'ابدأ محادثة سرية.' : 'Start a confidential conversation.'} data-id="element-80" />
            <div className="mt-8 space-y-6 text-sm text-slate-600" data-id="element-81">
              <div className="flex gap-4" data-id="element-82">
                <MapPinIcon className="shrink-0 text-[#a47b2a]" data-id="element-83" />
                <p data-id="element-84">
                  {ar ? <>
                      طريق المدينة، العيسائي، جدة 23346
                      <br data-id="element-85" />
                      المملكة العربية السعودية
                    </> : <>
                      Al-Madinah Road, Alesayi, Jeddah 23346
                      <br data-id="element-86" />
                      Kingdom of Saudi Arabia
                    </>}
                </p>
              </div>
              <div className="flex gap-4" data-id="element-87">
                <PhoneIcon className="shrink-0 text-[#a47b2a]" data-id="element-88" />
                <a href="tel:+966572787010" className="hover:text-[#a47b2a]" data-id="element-89">
                  +966 57 278 7010
                </a>
              </div>
              <div className="flex gap-4" data-id="element-90">
                <MailIcon className="shrink-0 text-[#a47b2a]" data-id="element-91" />
                <a href="mailto:info@unitedthirdfinancialcompany.com" className="hover:text-[#a47b2a]" data-id="element-92">
                  info@unitedthirdfinancialcompany.com
                </a>
              </div>
              <div className="flex gap-4" data-id="element-93">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#a47b2a] text-[9px] font-bold text-[#a47b2a]" data-id="element-94">
                  CR
                </span>
                <p data-id="element-95">
                  {ar ? 'رقم السجل التجاري: 4030535994' : 'CR No: 4030535994'}
                </p>
              </div>
              <div className="flex gap-4" data-id="element-96">
                <Clock3Icon className="shrink-0 text-[#a47b2a]" data-id="element-97" />
                <p data-id="element-98">
                  {ar ? 'الأحد–الخميس: 9:00 ص – 5:00 م' : 'Sunday–Thursday: 9:00 AM – 5:00 PM'}
                </p>
              </div>
              <a href="https://www.linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="inline-flex border border-slate-300 p-2 text-[#08233f] hover:border-[#b89142] hover:text-[#a47b2a]" data-id="element-99">
                <LinkedinIcon size={18} data-id="element-100" />
              </a>
            </div>
            <div className="mt-10 overflow-hidden border border-slate-200 bg-[#f2f4f5]" data-id="element-101">
              <iframe title={ar ? 'موقع مكتب جدة على الخريطة' : 'Jeddah office location map'} src="https://www.google.com/maps?q=Al-Madinah%20Road%2C%20Alesayi%2C%20Jeddah%2023346%2C%20Saudi%20Arabia&z=14&output=embed" className="h-56 w-full border-0 sm:h-64" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-102" />
              <a href="https://www.google.com/maps/search/?api=1&query=Al-Madinah+Road,+Alesayi,+Jeddah+23346,+Saudi+Arabia" target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 text-xs font-bold text-[#8c671e] transition-colors hover:bg-[#f7f1e4] hover:text-[#08233f]" aria-label="Open Riyadh office location in Google Maps" data-id="element-103">
                <span data-id="element-104">
                  {ar ? 'فتح الموقع في خرائط Google' : 'Open location in Google Maps'}
                </span>
                <MapPinIcon size={16} data-id="element-105" />
              </a>
            </div>
          </div>
          <form onSubmit={submit} noValidate className="bg-[#f4f5f6] p-6 sm:p-9" data-id="element-106">
            <h2 className="font-serif text-2xl" data-id="element-107">
              {ar ? 'إرسال استفسار' : 'Send an enquiry'}
            </h2>
            <p className="mt-2 text-sm text-slate-600" data-id="element-108">
              {ar ? 'سيتم التعامل مع معلوماتك بسرية.' : 'Your information will be treated confidentially.'}
            </p>
            <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-109">
              <Field label={label('Full name', 'الاسم الكامل')} name="name" data-id="element-110" />
              <Field label={label('Business email', 'البريد الإلكتروني للعمل')} name="email" type="email" data-id="element-111" />
              <Field label={label('Company', 'الشركة')} name="company" data-id="element-112" />
              <div data-id="element-113">
                <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#29455f]" data-id="element-114">
                  {label('Funding requirement', 'الاحتياج التمويلي')}
                </label>
                <select name="requirement" className="w-full border border-slate-300 bg-white p-3 text-sm outline-none focus:border-[#b89142]" data-id="element-115">
                  <option data-id="element-116">{label('Select an option', 'اختر خياراً')}</option>
                  <option data-id="element-117">{label('Commercial loan', 'قرض تجاري')}</option>
                  <option data-id="element-118">{label('Corporate finance', 'تمويل الشركات')}</option>
                  <option data-id="element-119">{label('Project finance', 'تمويل المشاريع')}</option>
                  <option data-id="element-120">{label('Other', 'أخرى')}</option>
                </select>
              </div>
            </div>
            <div className="mt-5" data-id="element-121">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#29455f]" data-id="element-122">
                {label('How can we help?', 'كيف يمكننا المساعدة؟')}
              </label>
              <textarea required name="message" rows={5} className="w-full border border-slate-300 bg-white p-3 text-sm outline-none focus:border-[#b89142]" data-id="element-123" />
            </div>
            {status === 'error' && <p role="alert" className="mt-4 text-sm text-red-700" data-id="element-124">
                {errorType === 'validation' ? label('Please complete your name, email, and message.', 'يرجى إكمال الاسم والبريد الإلكتروني والرسالة.') : label('We could not send your enquiry. Please try again or contact us by phone or email.', 'تعذر إرسال استفسارك. يرجى المحاولة مرة أخرى أو التواصل معنا عبر الهاتف أو البريد الإلكتروني.')}
              </p>}
            {status === 'success' && <p role="status" className="mt-4 flex items-center gap-2 text-sm text-emerald-700" data-id="element-125">
                <CheckCircle2Icon size={18} data-id="element-126" />
                {label('Thank you. Your enquiry has been received.', 'شكراً لك. تم استلام استفسارك.')}
              </p>}
            <button type="submit" disabled={status === 'sending'} className="mt-6 bg-[#08233f] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#b89142] disabled:cursor-wait disabled:opacity-70" data-id="element-127">
              {status === 'sending' ? label('Sending…', 'جارٍ الإرسال…') : label('Submit enquiry', 'إرسال الاستفسار')}
            </button>
          </form>
        </div>
      </section>
      <section className="bg-[#b89142] py-16" data-id="element-128">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8" data-id="element-129">
          <p className="text-xs font-bold uppercase tracking-[.2em] text-[#08233f]" data-id="element-130">
            {ar ? 'الخطوة التالية' : 'The next step'}
          </p>
          <h2 className="mx-auto mt-3 max-w-3xl font-serif text-3xl text-[#08233f]" data-id="element-131">
            {ar ? 'ندعو الشركات والمستثمرين ومالكي المشاريع لفتح حوار حول فرص التمويل.' : 'We invite businesses, investors, and project owners to open a dialogue about financing opportunities.'}
          </h2>
        </div>
      </section>
    </>;
}
function Field({
  label,
  name,
  type = 'text'
}: {
  label: string;
  name: string;
  type?: string;
}) {
  return <div data-id="element-132">
      <label htmlFor={name} className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#29455f]" data-id="element-133">
        {label}
      </label>
      <input id={name} required={name === 'name' || name === 'email'} type={type} name={name} className="w-full border border-slate-300 bg-white p-3 text-sm outline-none focus:border-[#b89142]" data-id="element-134" />
    </div>;
}